import { resetCursorVisibility } from "../lib/cursorVisibility";
import { h1 } from "../lib/markup/tags";
import { runFellowRealityChecks } from "../services/fellowRealityCheck";
import { notifyAdaptiveMissionComplete } from "../services/adaptiveTrialsService";
import CustomFreeSortPlugin from "../customPlugin/customFreeSort";
import { buildEndProcedure } from "./procedures/endProcedure";
import { buildMissionZero } from "./procedures/missionZero";
import { buildMissionOne } from "./procedures/missionOne";
import { buildMissionTwo } from "./procedures/missionTwo";
import { buildMissionThree } from "./procedures/missionThree";
import { buildMissionFour } from "./procedures/missionFour";
import { buildMissionFive } from "./procedures/missionFive";
import { buildStartProcedure } from "./procedures/startProcedure";
import { setAiAgentMissionNumber } from "./procedures/aiAgent/aiAgentConfig";
import { getDebugOverlay } from "./ui/debugOverlay";
import { blockFirefoxQuickFind } from "./utils/browserShortcuts";
import { updateDebugKeyboardLayout, updateDebugTrial } from "./ui/debugHelpers";

// Plugins whose trials require dragging items with the mouse - the cursor must stay visible
// during these regardless of trialConfig, since trialType-based inheritance through nested
// timelines (see feedback.js's quiz procedures) isn't reliable enough on its own.
const DRAGGABLE_TRIAL_TYPES = [CustomFreeSortPlugin];

// Keyed by missionNumber, so the mission-complete POST below only goes out once per
// mission (every phase of the blockType-98 trial shares the same trialConfig and would
// otherwise re-trigger it).
const adaptiveMissionCompleteNotified = {};

/**
 * Experiment-wide settings for jsPsych: https://www.jspsych.org/7.3/overview/experiment-options/
 * Note that Ecosystem combines these with other options required for Ecosystem to operate correctly
 */
export const jehliGearshiftTaskOptions = {
  show_progress_bar: false,
  // Called when every trial starts
  on_trial_start: function (trial) {
    // Update debug overlay with trial information
    const debugOverlay = getDebugOverlay();
    if (debugOverlay) {
      const trialData = trial.data || {};
      const trialConfig = trialData.trialConfig || {};

      // Get correct keycode and convert to char
      const correctKeycode = trialConfig.correctKeycode || "-";
      const correctKeychar = trialConfig.correctKeychar || "-";

      // Update trial info (trial type and correct key info)
      updateDebugTrial(
        debugOverlay,
        trialConfig.trialNumber,
        trialConfig.trialType || "-",
        correctKeycode,
        correctKeychar
      );
    }

    const trialType = trial?.data?.trialConfig?.trialType;
    const isDraggableTrial = DRAGGABLE_TRIAL_TYPES.includes(trial.type);
    const shouldShowCursor = !trialType || trialType === "FEEDBACK" || isDraggableTrial;

    if (shouldShowCursor) {
      document.body.classList.remove("hide-cursor");
    } else {
      document.body.classList.add("hide-cursor");
    }
  },
  // Called when every trial finishes
  on_trial_finish: function (data) {
    console.log(`Trial ${data.internal_node_id} just finished:`, data);
    // Update keyboard layout in debug overlay after detection trial
    if (data.name === "keyboardLayoutDetection" && data.layout_config_id) {
      const debugOverlay = getDebugOverlay();
      if (debugOverlay) {
        updateDebugKeyboardLayout(debugOverlay, data.layout_config_id);
      }
    }

    // blockType 98 marks the mission's last adaptive trial (adaptiveMissionTrials.js).
    // We notify from here, right after this trial's own result is recorded, instead of
    // from on_timeline_finish: a crash/low-score ending calls jsPsych.endExperiment(),
    // which jumps straight to the timeline root and skips on_timeline_finish entirely.
    //
    // data.missionIsAdaptive is checked too, since a static config can reuse blockType 98
    // for other reasons (e.g. mission0_ai_agent.json) - that must not send a real POST.
    const missionNumber = data.missionNumber;
    if (
      data.missionIsAdaptive &&
      data.trialConfig?.validation?.blockType === 98 &&
      !adaptiveMissionCompleteNotified[missionNumber]
    ) {
      adaptiveMissionCompleteNotified[missionNumber] = true;
      notifyAdaptiveMissionComplete({
        participantID: data.participant_id,
        startDate: data.start_date,
        missionNumber,
        block: data.trialConfig.block,
        trialNumber: data.trialConfig.trialNumber,
      });
    }
  },
  // Called when the experiment finishes
  on_finish: function (data) {
    console.log("The experiment has finished:", data);
    // Guarantee the pointer is visible again for the next Subject/Session ID entry
    document.body.classList.remove("hide-cursor");
    resetCursorVisibility();
    // Reload the page for another run-through of the experiment
    // window.location.reload();
  },
};

/**
 * This timeline builds the example reaction time task from the jsPsych tutorial.
 * Take a look at how the code here compares to the jsPsych documentation!
 * See the jsPsych documentation for more: https://www.jspsych.org/7.3/tutorials/rt-task/
 *
 * @param {JsPsych} jsPsych jsPsych instance being used to run the task
 * @param {Object} metadata The global metadata of the experiment
 * @param {string} studyID The ID of the study that was just logged into
 * @param {string} participantID The ID of the participant that was just logged in
 * @param {string} sessionID The ID of the current session that was just logged in
 * @param {string} startDate The ISO timestamp marking when this experiment session started
 * @returns {Object} A jsPsych timeline object
 */

export async function buildJehliGearshiftTaskTimeline(
  jsPsych,
  metadata,
  studyID,
  participantID,
  sessionID,
  startDate
) {
  blockFirefoxQuickFind();
  await runFellowRealityChecks(participantID, startDate);

  // Ensure the pointer is visible at the start of the game
  document.body.classList.remove("hide-cursor");
  resetCursorVisibility();

  // Build the start procedure
  const startProcedure = buildStartProcedure(jsPsych, metadata, studyID, participantID, sessionID);

  const missionNumberEnv = process.env.REACT_APP_MISSION_NUMBER
    ? parseInt(process.env.REACT_APP_MISSION_NUMBER, 10)
    : null;
  const hasMissionNumberEnv = Number.isInteger(missionNumberEnv);
  const useSingleMission = process.env.REACT_APP_SINGLE_MISSION === "true" || hasMissionNumberEnv;

  if (useSingleMission) {
    const missionNumber = hasMissionNumberEnv ? missionNumberEnv : 1;
    console.log(`Single-mission mode enabled: ${missionNumber}`);

    const missionTimelines = [
      trackAiAgentMission(await buildMissionByNumber(missionNumber, jsPsych, metadata, participantID)),
    ];
    const loadedMissionFiles = missionTimelines
      .map((timeline) => timeline?.data?.missionFile)
      .filter((missionFile) => Boolean(missionFile));
    if (loadedMissionFiles.length > 0) {
      console.log(`Loaded mission configs: ${loadedMissionFiles.join(", ")}`);
    }

    const endProcedure = buildEndProcedure(jsPsych, metadata, studyID, participantID, sessionID);
    const timeline = [startProcedure, ...missionTimelines, endProcedure];
    return timeline;
  }

  // Build the mission procedures
  const missionZero = await buildMissionZero(jsPsych, metadata, participantID);
  const missionOne = await buildMissionOne(jsPsych, metadata);
  const missionTwo = await buildMissionTwo(jsPsych, metadata);
  const missionThree = await buildMissionThree(jsPsych, metadata, participantID);
  const missionFour = await buildMissionFour(jsPsych, metadata);
  const missionFive = await buildMissionFive(jsPsych, metadata);

  const loadedMissionFiles = [
    missionZero?.data?.missionFile,
    missionOne?.data?.missionFile,
    missionTwo?.data?.missionFile,
    missionThree?.data?.missionFile,
    missionFour?.data?.missionFile,
    missionFive?.data?.missionFile,
  ].filter((missionFile) => Boolean(missionFile));
  if (loadedMissionFiles.length > 0) {
    console.log(`Loaded mission configs: ${loadedMissionFiles.join(", ")}`);
  }

  // Build the end procedure
  const endProcedure = buildEndProcedure(jsPsych, metadata, studyID, participantID, sessionID);

  // TODO: What's the best place to put this? How to get it in the data?
  const globalKeypresses = [];
  jsPsych.pluginAPI.getKeyboardResponse({
    callback_function: (keypress) => {
      globalKeypresses.push(keypress);

      // End the experiment
      if (jsPsych.pluginAPI.compareKeys(keypress.key, "q")) {
        jsPsych.data.addProperties({ experimentCompleted: false }); // TODO: Add this as "true" in the final trial of the experiment
        jsPsych.endExperiment(h1("Quit! Thank you for your participation"), {
          userCancelledExperiment: true,
        });
      }
    },
    persist: true,
    allow_held_key: true,
  });

  const timeline = [
    startProcedure,
    ...[missionZero, missionOne, missionTwo, missionThree, missionFour, missionFive].map(trackAiAgentMission),
    endProcedure,
  ];

  return timeline;
}

/**
 * Tells the aiAgent layer which mission is running once its timeline starts - online builds have
 * no REACT_APP_MISSION_NUMBER, and the agent needs the mission on session.start. Skipped
 * missions have an empty timeline and never start, so they're left alone.
 */
function trackAiAgentMission(missionTimeline) {
  const missionNumber = missionTimeline?.data?.missionNumber;
  if (!missionTimeline || missionTimeline.data?.skipped || !Number.isInteger(missionNumber)) return missionTimeline;
  const onTimelineStart = missionTimeline.on_timeline_start;
  missionTimeline.on_timeline_start = (...args) => {
    setAiAgentMissionNumber(missionNumber);
    return onTimelineStart?.(...args);
  };
  return missionTimeline;
}

async function buildMissionByNumber(missionNumber, jsPsych, metadata, participantID) {
  switch (missionNumber) {
    case 0:
      return buildMissionZero(jsPsych, metadata, participantID);
    case 1:
      return buildMissionOne(jsPsych, metadata);
    case 2:
      return buildMissionTwo(jsPsych, metadata);
    case 3:
      return buildMissionThree(jsPsych, metadata, participantID);
    case 4:
      return buildMissionFour(jsPsych, metadata);
    case 5:
      return buildMissionFive(jsPsych, metadata);
    default:
      console.warn(`Unknown mission number: ${missionNumber}. Defaulting to mission 1.`);
      return buildMissionOne(jsPsych, metadata);
  }
}
